import { api } from './authService';

export interface Clinic {
  id: string;
  name: string;
  address: {
    street: string;
    streetNumber?: string;
    city: string;
    county: string;
    country: string;
    postalCode?: string;
  };
  phone: string;
  email: string;
  website?: string;
  description?: string;
  schedule?: {
    [key: string]: {
      isOpen: boolean;
      openTime: string;
      closeTime: string;
    };
  };
  services?: string[];
  status: string;
  adminId?: string;
  adminName?: string;
  veterinarianCount?: number;
  appointmentCount?: number;
  revenue?: number;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateClinicData {
  name: string;
  address: {
    street: string;
    streetNumber?: string;
    city: string;
    county: string;
    country: string;
    postalCode?: string;
  };
  phone: string;
  email: string;
  website?: string;
  description?: string;
  schedule?: {
    [key: string]: {
      isOpen: boolean;
      openTime: string;
      closeTime: string;
    };
  };
  services?: string[];
  adminId?: string;
  status?: string;
}

export interface UpdateClinicData {
  name?: string;
  address?: {
    street: string;
    streetNumber?: string;
    city: string;
    county: string;
    country: string;
    postalCode?: string;
  };
  phone?: string;
  email?: string;
  website?: string;
  description?: string;
  schedule?: {
    [key: string]: {
      isOpen: boolean;
      openTime: string;
      closeTime: string;
    };
  };
  services?: string[];
  adminId?: string;
  status?: string;
}

export interface ClinicsFilters {
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

const clinicsService = {
  // Obtine lista clinicilor
  async getClinics(filters: ClinicsFilters = {}): Promise<{ clinics: Clinic[]; total: number; page: number; totalPages: number }> {
    try {
      const response = await api.get('/clinics', { params: filters });
      
      // API-ul returneaza {success: true, data: Array, pagination: {...}}
      const clinics = response.data.data || [];
      const pagination = response.data.pagination || {};
      
      return {
        clinics: clinics,
        total: pagination.total || clinics.length,
        page: pagination.page || 1,
        totalPages: pagination.totalPages || 1
      };
    } catch (error) {
      throw error;
    }
  },

  // Obtine o clinica dupa ID
  async getClinic(id: string): Promise<Clinic> {
    try {
      const response = await api.get(`/clinics/${id}`);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Creeaza o clinica noua
  async createClinic(clinicData: CreateClinicData): Promise<Clinic> {
    try {
      const response = await api.post('/clinics', clinicData);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Actualizeaza o clinica
  async updateClinic(id: string, clinicData: UpdateClinicData): Promise<Clinic> {
    try {
      const response = await api.put(`/clinics/${id}`, clinicData);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Sterge o clinica
  async deleteClinic(id: string): Promise<void> {
    try {
      // Backend-ul nu are endpoint DELETE pentru clinici
      // Implementam o solutie temporara prin actualizarea statusului la 'inactive'
      
      // Verifica daca utilizatorul curent are permisiunile necesare
      // Endpoint-ul permite doar MASTER_ADMIN
      await api.patch(`/clinics/${id}/status`, { status: 'inactive' });
    } catch (error: any) {
      // Verifica daca eroarea este legata de autorizare
      if (error.response?.status === 403) {
        throw new Error('Nu aveti permisiunea sa stergeti aceasta clinica. Doar Master Admin poate efectua aceasta operatie.');
      }
      
      throw error;
    }
  },

  // Actualizeaza statusul unei clinici
  async updateClinicStatus(id: string, status: string): Promise<Clinic> {
    try {
      const response = await api.patch(`/clinics/${id}/status`, { status });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtine statistici despre clinici
  async getClinicStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
    totalVeterinarians: number;
    totalAppointments: number;
    totalRevenue: number;
    byStatus: { [key: string]: number };
  }> {
    try {
      // Backend-ul nu are endpoint pentru statistici, calculam din datele clinicilor
      const clinicsResponse = await api.get('/clinics');
      const clinics = clinicsResponse.data.data || [];
      
      const stats = {
        total: clinics.length,
        active: clinics.filter((c: Clinic) => c.status === 'active').length,
        inactive: clinics.filter((c: Clinic) => c.status === 'inactive').length,
        totalVeterinarians: clinics.reduce((sum: number, c: Clinic) => sum + (c.veterinarianCount || 0), 0),
        totalAppointments: clinics.reduce((sum: number, c: Clinic) => sum + (c.appointmentCount || 0), 0),
        totalRevenue: clinics.reduce((sum: number, c: Clinic) => sum + (c.revenue || 0), 0),
        byStatus: {} as { [key: string]: number }
      };
      
      // Calculeaza statistici pe status
      clinics.forEach((clinic: Clinic) => {
        stats.byStatus[clinic.status] = (stats.byStatus[clinic.status] || 0) + 1;
      });
      
      return stats;
    } catch (error) {
      throw error;
    }
  },

  /** Veterinari activi ai clinicii — aliniat la GET /api/v1/clinics/:clinicId/vets */
  async getClinicVeterinarians(clinicId: string): Promise<any[]> {
    try {
      const response = await api.get(`/clinics/${clinicId}/vets`);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Obtine programarile unei clinici
  async getClinicAppointments(clinicId: string, filters: any = {}): Promise<any> {
    try {
      const response = await api.get(`/clinics/${clinicId}/appointments`, { params: filters });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Exporta clinicile in format CSV
  async exportClinics(filters: ClinicsFilters = {}): Promise<Blob> {
    try {
      // Backend-ul nu are endpoint pentru export, generam CSV local
      const clinicsResponse = await api.get('/clinics', { params: filters });
      const clinics = clinicsResponse.data.data || [];
      
      // Genereaza CSV
      const headers = ['ID', 'Nume', 'Adresa', 'Telefon', 'Email', 'Status', 'Admin', 'Veterinari', 'Programari', 'Venituri', 'Data Crearii'];
      const csvContent = [
        headers.join(','),
        ...clinics.map((clinic: Clinic) => [
          clinic.id,
          clinic.name,
          `${clinic.address.street} ${clinic.address.streetNumber || ''}, ${clinic.address.city}, ${clinic.address.county}, ${clinic.address.country}, ${clinic.address.postalCode || ''}`.replace(/(, )+/g, ', ').replace(/^, |, $/g, ''),
          clinic.phone,
          clinic.email,
          clinic.status,
          clinic.adminName || '',
          clinic.veterinarianCount || 0,
          clinic.appointmentCount || 0,
          clinic.revenue || 0,
          clinic.createdAt
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      return blob;
    } catch (error) {
      throw error;
    }
  }
};

export default clinicsService; 
